import { useCallback, useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { useRemixerAudioContext } from 'pages/Remixer/hooks';

const usePlayer = () => {
  const ref = useRef<HTMLElement>();

  const [wavesurfer, setWavesurfer] = useState<WaveSurfer | null>(null);
  const { audioRef } = useRemixerAudioContext();

  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState<number>(0);

  useEffect(() => {
    if (!audioRef?.current || !ref.current) {
      return;
    }

    const wavesurferInstance = WaveSurfer.create({
      container: ref.current,
      media: audioRef.current,
      // @ts-ignore
      splitChannels: false,
      mediaControls: false,
    });

    setWavesurfer(wavesurferInstance);

    return () => {
      wavesurferInstance.destroy();
      wavesurferInstance.unAll();
      setWavesurfer(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioRef?.current, ref, setWavesurfer]);

  useEffect(() => {
    if (!wavesurfer) {
      return;
    }
    wavesurfer.on('play', () => setIsPlaying(true));
    wavesurfer.on('pause', () => setIsPlaying(false));
    wavesurfer.on('timeupdate', (time) => setCurrentTime(Math.floor(time)));

    wavesurfer.on('loading', (percentsLoaded) =>
      setIsLoading(percentsLoaded !== 100)
    );
  }, [wavesurfer, setIsPlaying, setIsLoading]);

  const play = useCallback(() => wavesurfer?.play(), [wavesurfer]);
  const pause = useCallback(() => wavesurfer?.pause(), [wavesurfer]);

  return {
    ref,
    wavesurfer,
    play,
    pause,
    isPlaying,
    isLoading,
    currentTime,
  };
};

export default usePlayer;
